import {useQuery} from "@tanstack/react-query";
import {API} from "api";

/**
 * React Query wrappers for the server-side default-scene cascade
 * (GET /api/v3/default-scenes/resolved and /resolved-all).
 *
 * These replace the client-side cascade over the full override list: the server
 * runs resolveAllOverrides + ALL-products materialization + mergeScenes and
 * returns one merged scene (a few KB) instead of every row for the channel
 * (~200 KB). React Query is the only cache — nothing is mirrored into context.
 *
 * Both are inert until `enabled` (the bgDerivation flag) is true, so the legacy
 * path stays byte-identical with the flag off.
 */

/**
 * One key's cascade: {scene_data, exact_row, layers} plus
 * {product_default_scene, product_default_hash} — the products.js default scene
 * for the same key, which flag-on `useInitialScene` uses in place of its
 * `POST /scene/load/v2` (issue #567 A1).
 * staleTime 30s (#528, 2c) so a second admin's save converges on the next
 * refetch, replacing the old staleTime:Infinity that made saves invisible until
 * a full reload.
 */
export function useResolvedDefaultScene({
  channel,
  product,
  type,
  set,
  layout,
  background,
  variant,
  enabled = true,
}) {
  return useQuery({
    queryKey: [
      "v3-resolved-default",
      channel,
      product ?? null,
      type,
      // Stringified: `layout`/`background`/`variant` reach the two callers as a
      // number from settings context and as a string from the url, and a number
      // key would fetch the SAME url twice under two cache entries.
      String(set ?? ""),
      String(layout ?? ""),
      String(background ?? ""),
      String(variant ?? ""),
    ],
    queryFn: () =>
      API.fetchResolvedDefaultScene({channel, product, type, set, layout, background, variant}),
    enabled: Boolean(enabled && channel && type),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Every product of a (channel, type) in one response:
 * {scenes, bg_options, all_products_bg_options}. `bg_options` /
 * `all_products_bg_options` are the two lookups the ALL-products carousel
 * initializes from — they're separate from `scenes` because they come from the
 * single best-matching ROW, not the merged cascade (a global bgOption must not
 * shadow the ALL editor's per-product map).
 *
 * Only the ALL-products aggregate view needs this — hence `enabled` should also
 * carry `product === "ALL"` at the call site. It has no `variant`: the brand
 * treatment belongs to one product (JetBrains Air) and the aggregate has no
 * treatment control, so its cascade reads the treatment-free rows.
 *
 * The options builder is separate so useQueries callers (one query per child
 * carousel variation in useEffectiveBgTransform) share the exact key shape —
 * a second hand-written key here is how R5's invalidation drift started.
 */
export function resolvedDefaultScenesAllQueryOptions({channel, type, set, layout, background}) {
  return {
    queryKey: [
      "v3-resolved-default-all",
      channel,
      type,
      String(set ?? ""),
      String(layout ?? ""),
      String(background ?? ""),
    ],
    queryFn: () => API.fetchResolvedDefaultScenesAll({channel, type, set, layout, background}),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  };
}

export function useResolvedDefaultScenesAll({channel, type, set, layout, background, enabled = true}) {
  return useQuery({
    ...resolvedDefaultScenesAllQueryOptions({channel, type, set, layout, background}),
    enabled: Boolean(enabled && channel && type),
  });
}

/**
 * The resolved cascade for each background of one route, as
 * {[background]: /resolved response}. The background selector draws each tile
 * with its own default crop from it; the current background's own query above
 * stays the source for the banners.
 *
 * The key starts with "v3-resolved-default", so a default-scene save, which
 * invalidates that prefix, refreshes the tiles too.
 */
export function useResolvedDefaultScenesBatch({
  channel,
  product,
  type,
  set,
  layout,
  variant,
  backgrounds,
  enabled = true,
}) {
  return useQuery({
    queryKey: [
      "v3-resolved-default",
      "batch",
      channel,
      product ?? null,
      type,
      String(set ?? ""),
      String(layout ?? ""),
      String(variant ?? ""),
      backgrounds,
    ],
    queryFn: async () => {
      const {results} = await API.fetchResolvedDefaultScenesBatch({
        channel,
        product,
        type,
        set,
        layout,
        variant,
        backgrounds,
      });
      if (!results || typeof results !== "object") {
        throw new Error("Invalid resolved-batch response — results missing");
      }
      return results;
    },
    enabled: Boolean(enabled && channel && type && backgrounds.length > 0),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}
